import { getRequest, postRequest } from '../base';
import { AxiosResponse } from 'axios';
import { PaginationInfo } from 'hooks/serverPagination/types';
import {
  RCustomer,
  RCustomerBills,
  RCustomerLedger,
  RCustomerReturn,
  RCustomerVoucher,
  RLedgerDetails as RCusLedgerDetails,
  TCustomer,
  RCustomerMember,
} from '../types/customer.types';
import { Customer } from 'components/Common/CustomerSearchMultiple/types';

export interface RCustomerLoyalty {
  cid: string;
  color: string;
  createdBy: string;
  createdById: string;
  createdOn: number;
  enabledLoyalty?: boolean;
  id: string;
  name: string;
  table: string;
  discount?: number;
}

export interface TFamily {
  customers: Customer[];
}

export const CustomerService = {
  list: async (): Promise<AxiosResponse<{ data: { customers: RCustomer } }>> =>
    getRequest('store_customers'),

  customerListing: async (
    paginationInfo: PaginationInfo,
    searchQuery: string,
    searchType: string,
    queryParams?: Record<string, string>,
  ): Promise<AxiosResponse> => {
    const { nextPageId, nextData } = paginationInfo;
    const baseUrl = 'new_customer_listing';
    const params = new URLSearchParams();
    if (nextPageId) {
      params.append('nextPageId', nextPageId);
    }
    if (nextData) {
      params.append('nextData', nextData);
    }
    if (searchQuery) {
      params.append('search', searchQuery);
    }
    if (searchQuery && searchType) {
      params.append('searchType', searchType);
    }
    // Add additional query parameters
    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (key.startsWith('filter_')) {
          params.append(key.replace('filter_', ''), value);
        }
      });
    }
    return getRequest(`${baseUrl}?${params.toString()}`);
  },
  get: async (customerId: string): Promise<AxiosResponse<{ data: RCustomer }>> =>
    getRequest(`customer/${customerId}`),

  create: async (data: TCustomer): Promise<AxiosResponse<{ data: { data: RCustomer } }>> =>
    postRequest('add_customer', data),
  update: async (customerId: string, data: TCustomer): Promise<AxiosResponse> =>
    postRequest(`update_customer/${customerId}`, data),
  detailsByType: async (
    customerId: string,
    type: 'bill' | 'return' | 'ledger' | 'voucher' | 'loyaltyLedger' | 'family',
    from: number,
    to: number,
    billType?: string,
  ): Promise<
    AxiosResponse<{
      data: RCustomerBills | RCustomerReturn | RCustomerLedger | RCustomerVoucher | RCustomerMember;
    }>
  > => {
    const billTypeStr = billType ? `&billType=${billType}` : '';
    return getRequest(
      `customer_bill/${customerId}?type=${type}&from=${from}&to=${to}${billTypeStr}`,
    );
  },
  ledgerDetails: async (ledgerId: string): Promise<AxiosResponse<{ data: RCusLedgerDetails }>> =>
    getRequest(`bill_ledger_details/${ledgerId}`),
  deletePayment: async (paymentId: string): Promise<AxiosResponse> =>
    postRequest(`delete_bill_ledger/${paymentId}`, {}),
  downloadReport: async (): Promise<AxiosResponse<{ data: { report: string } }>> =>
    postRequest(`customer_report`),
  downloadLedger: async (
    customerId: string,
    from: number,
    to: number,
  ): Promise<AxiosResponse<{ data: { s3Link: string } }>> =>
    getRequest(`download_customer_ledger/${customerId}?from=${from}&to=${to}`),
  newCustomerSearch: async (
    key: string,
    search: string,
    type?: string,
  ): Promise<AxiosResponse<{ data: { items: RCustomer[] } }>> =>
    getRequest(
      `customer_search?key=${key}&search=${encodeURIComponent(search)}${
        type ? '&type=' + type : ''
      }`,
    ),
  whatsappReminder: async (customerId: string): Promise<AxiosResponse> =>
    postRequest(`payment_reminder_share/${customerId}`, {}),
  deleteFamilyMember: async (cId: string): Promise<AxiosResponse> =>
    postRequest(`remove_customer_family/${cId}`),
};

export const CustomerFamilyService = {
  create: async (data: TFamily): Promise<AxiosResponse<{ data: { customers: RCustomer } }>> =>
    postRequest('update_customer_family', data),
  detailsByType: async (
    customerFamilyId: string,
    type?: 'bill' | 'return' | 'ledger' | 'loyaltyLedger' | undefined,
    from?: number,
    to?: number,
  ): Promise<AxiosResponse> =>
    getRequest(
      `family_details/${customerFamilyId}?${type ? `type=${type}&` : ''}from=${from}&to=${to}`,
    ),
};

export const CustomerLoyaltyService = {
  create: async (data: unknown): Promise<AxiosResponse<{ data: RCustomerLoyalty }>> =>
    postRequest(`create_customer_profile`, data),

  list: async (): Promise<AxiosResponse<{ data: { items: RCustomerLoyalty[] } }>> =>
    getRequest(`customer_profile_listing`),

  get: async (id: string): Promise<AxiosResponse<{ data: { items: RCustomerLoyalty } }>> =>
    getRequest(`get_customer_profile/${id}`),

  update: async (id: string, data: unknown): Promise<AxiosResponse<{ data: RCustomerLoyalty }>> =>
    postRequest(`update_customer_profile/${id}`, data),

  delete: async (id: string): Promise<AxiosResponse> =>
    postRequest(`delete_customer_profile/${id}`, {}),
};

export type RBulkCustomer = RCustomer & {
  profileId?: string;
};

export const BulkCustomerService = {
  getAll: (ids: string[]): Promise<AxiosResponse<{ data: { items: RBulkCustomer[] } }>> =>
    postRequest(`get_bulk_customers`, { ids }),

  update: (data: unknown) => postRequest(`bulk_customer_update`, data),
};
